"use client"

import React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Handle, Position } from "reactflow"
import { ActivityIcon as Function, Minus, Plus, Trash2 } from "lucide-react"
import "./Node.css"
import "./ActionNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))
const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const ActionNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, deleteNode, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [actionData, setActionData] = useState(() => ({
    label: ensureString(data.label) || "Set Suppression",
    name: ensureString(data.name) || ensureString(data.label) || "Set Suppression",
    description: ensureString(data.description),
    operation: ensureString(data.operation) || getSettingValue(settings, "operation", "set_suppression") || "set_suppression",
    entityType: ensureString(data.entityType) || getSettingValue(settings, "entity_type", "contract") || "contract",
    entityKeySource: ensureString(data.entityKeySource) || getSettingValue(settings, "entity_key_source", "variable") || "variable",
    entityKeyVariable: ensureString(data.entityKeyVariable) || getSettingValue(settings, "entity_key_variable", "contractid") || "contractid",
    suppressionType: ensureString(data.suppressionType) || getSettingValue(settings, "suppression_type", "workflow_task_creation") || "workflow_task_creation",
    reasonCode: ensureString(data.reasonCode) || getSettingValue(settings, "reason_code", "wait_full_project_completion") || "wait_full_project_completion",
    reasonText: ensureString(data.reasonText) || getSettingValue(settings, "reason_text", ""),
    scopeType: ensureString(data.scopeType) || getSettingValue(settings, "scope_type", "workflow") || "workflow",
    expanded,
  }))
  const actionDataRef = useRef(actionData)

  useEffect(() => {
    const nextSettings = data?.settings || {}
    const nextData = {
      label: ensureString(data.label) || "Set Suppression",
      name: ensureString(data.name) || ensureString(data.label) || "Set Suppression",
      description: ensureString(data.description),
      operation: ensureString(data.operation) || getSettingValue(nextSettings, "operation", "set_suppression") || "set_suppression",
      entityType: ensureString(data.entityType) || getSettingValue(nextSettings, "entity_type", "contract") || "contract",
      entityKeySource: ensureString(data.entityKeySource) || getSettingValue(nextSettings, "entity_key_source", "variable") || "variable",
      entityKeyVariable: ensureString(data.entityKeyVariable) || getSettingValue(nextSettings, "entity_key_variable", "contractid") || "contractid",
      suppressionType: ensureString(data.suppressionType) || getSettingValue(nextSettings, "suppression_type", "workflow_task_creation") || "workflow_task_creation",
      reasonCode: ensureString(data.reasonCode) || getSettingValue(nextSettings, "reason_code", "wait_full_project_completion") || "wait_full_project_completion",
      reasonText: ensureString(data.reasonText) || getSettingValue(nextSettings, "reason_text", ""),
      scopeType: ensureString(data.scopeType) || getSettingValue(nextSettings, "scope_type", "workflow") || "workflow",
      expanded: data.expanded ?? true,
    }
    setActionData(nextData)
  }, [data.label, data.name, data.description, data.operation, data.entityType, data.entityKeySource, data.entityKeyVariable, data.suppressionType, data.reasonCode, data.reasonText, data.scopeType, data.expanded, data.settings, id])

  useEffect(() => {
    actionDataRef.current = actionData
  }, [actionData])

  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...actionDataRef.current,
      [field]: ensureString(value),
    }
    if (field === "operation" && ensureString(value) === "bypass_wait") {
      updated.suppressionType = "wait_bypass"
      if (!updated.reasonCode || updated.reasonCode === "wait_full_project_completion") {
        updated.reasonCode = "wait_bypass"
      }
    }
    // Keep label synchronized with name for node title consistency.
    if (field === "name") {
      updated.label = ensureString(value) || "Set Suppression"
    }
    setActionData(updated)

    if (persistOnChange) {
      updateWorkflowContext(updated)
    }

    if (persistOnChange && data.onNodeDataChange) {
      data.onNodeDataChange(id, {
        ...updated,
        expanded,
      })
    }
  }

  const handleBlur = () => {
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = actionDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) {
        persist(nodeId, {
          ...current,
          expanded,
        })
      }
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".action-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }

    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  return (
    <div
      className={`trigger-node-container action-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "action-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Function size={16} className="trigger-icon" />
          <span>{actionData.name && actionData.name.trim() !== "" ? actionData.name : "Set Suppression"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="actionNode"
            nodeName={actionData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm("Are you sure you want to delete this node?")) {
                try {
                  if (deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    throw new Error("Unable to delete node: missing deleteNode function or currentWorkflow")
                  }
                } catch (error) {
                  console.error("Failed to delete node:", error)
                  alert("Failed to delete node. Please try again.")
                }
              }
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="node-field">
            <label className="dark:text-white">Action Name</label>
            <input
              value={actionData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={handleBlur}
              placeholder="Set Suppression"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Description</label>
            <input
              value={actionData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              onBlur={handleBlur}
              placeholder="Suppress task creation for this contract"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Operation</label>
            <select
              value={actionData.operation}
              onChange={(e) => handleChange("operation", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none nodrag"
            >
              <option value="set_suppression">Set Suppression</option>
              <option value="clear_suppression">Clear Suppression</option>
              <option value="bypass_wait">Bypass Wait</option>
            </select>
          </div>
          <div className="node-field">
            <label className="dark:text-white">Entity Type</label>
            <select
              value={actionData.entityType}
              onChange={(e) => handleChange("entityType", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none nodrag"
            >
              <option value="contract">Contract</option>
            </select>
          </div>
          <div className="node-field">
            <label className="dark:text-white">Entity Key Source</label>
            <select
              value={actionData.entityKeySource}
              onChange={(e) => handleChange("entityKeySource", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none nodrag"
            >
              <option value="variable">Variable</option>
            </select>
          </div>
          <div className="node-field">
            <label className="dark:text-white">Entity Key Variable</label>
            <input
              value={actionData.entityKeyVariable}
              onChange={(e) => handleChange("entityKeyVariable", e.target.value)}
              onBlur={handleBlur}
              placeholder="contractid"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Suppression Type</label>
            <input
              value={actionData.suppressionType}
              onChange={(e) => handleChange("suppressionType", e.target.value)}
              onBlur={handleBlur}
              placeholder="workflow_task_creation"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Reason Code</label>
            <input
              value={actionData.reasonCode}
              onChange={(e) => handleChange("reasonCode", e.target.value)}
              onBlur={handleBlur}
              placeholder="wait_full_project_completion"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Reason Text</label>
            <textarea
              value={actionData.reasonText}
              onChange={(e) => handleChange("reasonText", e.target.value)}
              onBlur={handleBlur}
              rows={3}
              placeholder="Optional note"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
          <div className="node-field">
            <label className="dark:text-white">Scope</label>
            <select
              value={actionData.scopeType}
              onChange={(e) => handleChange("scopeType", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none nodrag"
            >
              <option value="workflow">Workflow</option>
              <option value="global">Global</option>
            </select>
          </div>
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default ActionNode
